import React, { FC } from 'react';
import classnames from 'classnames';
import Downshift, { StateChangeOptions, DownshiftState } from 'downshift';
import {
  Asset,
  ArrowDownwardIcon,
  CloseIcon,
  Checkbox,
} from '@clearcover/atlas';
import styles from './styles.module.css';

export type ItemType = {
  label: string;
  key: string;
  selected: boolean;
};

type MultiSelectProps = {
  label: string;
  items: Array<ItemType>;
  onChangeSelection: (key: ItemType) => void;
};

/*
  The only reason we have this stateReducer is so that
  when you select one of the items from the menu, the menu
  stays open since it's a "multi" select.  By default, it would
  close otherwise.
*/
function stateReducer(
  _: DownshiftState<ItemType>,
  changes: StateChangeOptions<ItemType>
) {
  switch (changes.type) {
    case Downshift.stateChangeTypes.clickItem:
    case Downshift.stateChangeTypes.keyDownEnter: {
      return {
        ...changes,
        isOpen: true,
      };
    }
    default: {
      return changes;
    }
  }
}

export const MultiSelectInput: FC<MultiSelectProps> = ({
  onChangeSelection,
  items,
  label,
  ...props
}) => {
  const selectedItems = items.filter((i) => i.selected);

  return (
    <Downshift
      stateReducer={stateReducer}
      onChange={onChangeSelection}
      selectedItem={selectedItems}
      itemToString={(item) => item.key}
    >
      {({
        getToggleButtonProps,
        getItemProps,
        getMenuProps,
        isOpen,
        highlightedIndex,
      }) => {
        return (
          <div
            className={classnames(styles.select, { [styles.isOpen]: isOpen })}
          >
            <button {...getToggleButtonProps()} {...props}>
              <span className={styles.label}>{label}:</span>
              <div className={styles.selected}>
                {selectedItems.length > 0 ? (
                  selectedItems.slice(0, 2).map((item) => {
                    return (
                      <div className={styles.tag} key={item.key}>
                        <span>{item.label}</span>
                        <span
                          className={styles.close}
                          onClick={() => onChangeSelection(item)}
                        >
                          <Asset
                            icon={CloseIcon}
                            height={10}
                            width={10}
                            description="close"
                          />
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <span className={styles.placeholder}>all</span>
                )}

                {selectedItems.length > 2 && (
                  <div className={styles.tag}>
                    <span>more...</span>
                  </div>
                )}
              </div>
              <Asset
                icon={ArrowDownwardIcon}
                className={styles.arrow}
                height={10}
                width={10}
                description="dropdown arrow"
              />
            </button>
            {isOpen && (
              <ul {...getMenuProps()}>
                {items.map((item, index: number) => {
                  return (
                    <li
                      key={item.key}
                      className={styles.listItem}
                      data-testid={`option-${item.key}`}
                      {...getItemProps({ item, index })}
                    >
                      <Checkbox
                        label={item.label}
                        name={`option-${item.key}`}
                        value={item.selected}
                        onChange={() => {
                          // Intentionally empty; do nothing
                        }}
                        containerFocused={highlightedIndex === index}
                        readOnly
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      }}
    </Downshift>
  );
};
